import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Camera,
  Film,
  Sparkles,
  Maximize2,
  Play,
  Pause,
} from "lucide-react";
import { cn } from "@/lib/utils";

const AUTO_PLAY_INTERVAL = 4000; // 4 seconds per highlight in modal

/**
 * Parses video URL to determine whether to render responsive iframe embed (YouTube/Vimeo)
 * or native HTML5 video stream (MP4, WebM, Cloudinary, direct video URLs).
 */
function parseVideoSource(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();

  // 1. YouTube Matcher
  const ytMatch = trimmed.match(
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i
  );
  if (ytMatch && ytMatch[1]) {
    return {
      type: "youtube",
      embedUrl: `https://www.youtube-nocookie.com/embed/${ytMatch[1]}?autoplay=1&rel=0&modestbranding=1`,
    };
  }

  // 2. Vimeo Matcher
  const vimeoMatch = trimmed.match(
    /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|)(\d+)(?:$|\/|\?)/i
  );
  if (vimeoMatch && vimeoMatch[3]) {
    return {
      type: "vimeo",
      embedUrl: `https://player.vimeo.com/video/${vimeoMatch[3]}?autoplay=1&title=0&byline=0&portrait=0`,
    };
  }

  // 3. Direct HTML5 Video (MP4, WebM, OGG, Cloudinary video URL, etc.)
  return {
    type: "native",
    src: trimmed,
  };
}

/**
 * ============================================================
 * WORKSHOP MEDIA VIEWER (Proper Centered Modal Dialog)
 * ============================================================
 * Mounted via React Portal (createPortal) directly to document.body:
 * - High z-index (z-[9999]) completely isolated from page layout/transforms
 * - Automatically moves assets one by one with progress indicator & pause/resume
 * - Navigation buttons positioned prominently in the bottom middle for maximum visibility
 * - Centered dialog overlay with dark frosted glass backdrop (bg-black/90 backdrop-blur-2xl)
 * - Distinct modal card with rounded corners, border, and elevation
 * - Full image scaling and responsive video player
 * - Keyboard navigation (Arrow keys, Escape)
 * - Outside backdrop click dismissal and body scroll locking
 */
export default function WorkshopMediaViewer({
  isOpen,
  items = [],
  initialIndex = 0,
  onClose,
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef(null);
  const timerRef = useRef(null);
  const progressTimerRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Synchronize index when reopened with specific card
  useEffect(() => {
    if (typeof initialIndex === "number" && initialIndex >= 0 && initialIndex < items.length) {
      setCurrentIndex(initialIndex);
      setProgress(0);
    }
  }, [initialIndex, items.length, isOpen]);

  const hasMultiple = items.length > 1;

  const handlePrev = useCallback(() => {
    if (!hasMultiple) return;
    setCurrentIndex((curr) => (curr === 0 ? items.length - 1 : curr - 1));
    setProgress(0);
  }, [hasMultiple, items.length]);

  const handleNext = useCallback(() => {
    if (!hasMultiple) return;
    setCurrentIndex((curr) => (curr === items.length - 1 ? 0 : curr + 1));
    setProgress(0);
  }, [hasMultiple, items.length]);

  // Automatic progression in modal with progress bar
  useEffect(() => {
    if (!isOpen || !hasMultiple || isPaused) {
      if (timerRef.current) clearInterval(timerRef.current);
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      return;
    }

    const startTime = Date.now();
    setProgress(0);

    // Update progress bar every 40ms
    progressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(100, (elapsed / AUTO_PLAY_INTERVAL) * 100);
      setProgress(pct);
    }, 40);

    // Auto-advance step
    timerRef.current = setInterval(() => {
      setCurrentIndex((curr) => (curr === items.length - 1 ? 0 : curr + 1));
      setProgress(0);
    }, AUTO_PLAY_INTERVAL);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [isOpen, hasMultiple, isPaused, currentIndex, items.length]);

  // Keyboard navigation & body scroll locking
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNext();
      } else if (e.key === " ") {
        // Space toggles play/pause
        e.preventDefault();
        setIsPaused((p) => !p);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, onClose, handlePrev, handleNext]);

  if (!isOpen || !items || items.length === 0 || !mounted) {
    return null;
  }

  const activeItem = items[currentIndex] || items[0];
  if (!activeItem) return null;

  const isVideo = activeItem.media_format === "video" || Boolean(activeItem.video_url);
  const videoSource = isVideo ? parseVideoSource(activeItem.video_url || activeItem.storage_path) : null;
  const imageUrl = activeItem.storage_path || activeItem.url || "";
  const posterUrl = activeItem.thumbnail_url || imageUrl;
  const titleText = activeItem.title || activeItem.caption || "Session Highlight";
  const descText = activeItem.description || "";

  const handleBackdropClick = (e) => {
    if (e.target === containerRef.current) {
      onClose();
    }
  };

  const modalContent = (
    <div
      ref={containerRef}
      role="dialog"
      data-component="workshop-media-viewer"
      aria-modal="true"
      aria-label="Workshop Highlight Media Viewer"
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-2xl p-3 sm:p-6 md:p-8 animate-in fade-in duration-200 select-none"
    >
      {/* ── Main Modal Card ────────────────────────────────────────── */}
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        className="relative w-full max-w-5xl max-h-[92vh] flex flex-col rounded-3xl border border-white/20 bg-slate-950/95 shadow-[0_25px_80px_rgba(0,0,0,0.95)] overflow-hidden"
      >
        {/* ── Modal Top Bar ────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-3.5 sm:px-6 sm:py-4 border-b border-white/10 bg-white/[0.03]">
          {/* Left: Badge & Auto-Play Status */}
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white/90 border border-white/15 backdrop-blur-md">
              {isVideo ? (
                <>
                  <Film className="w-3.5 h-3.5 text-cyan-400" />
                  <span>Video Highlight</span>
                </>
              ) : (
                <>
                  <Camera className="w-3.5 h-3.5 text-cyan-400" />
                  <span>Photo Moment</span>
                </>
              )}
            </div>

            {hasMultiple && (
              <span className="text-xs font-semibold text-cyan-300 px-2.5 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-500/25">
                {currentIndex + 1} of {items.length}
              </span>
            )}
          </div>

          {/* Right: Close button with keyboard shortcut */}
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-block text-[11px] text-white/40 font-mono">
              [ESC to close]
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close modal"
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all border border-white/15 hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Main Stage / Media Area ─────────────────────────────────── */}
        <div className="relative flex-1 flex items-center justify-center w-full min-h-[300px] sm:min-h-[440px] max-h-[64vh] p-2 sm:p-6 bg-black/70 overflow-hidden">
          {/* Media Presentation Container */}
          <div className="w-full h-full flex items-center justify-center">
            {isVideo && videoSource ? (
              videoSource.type === "youtube" || videoSource.type === "vimeo" ? (
                /* Embeddable Iframe (YouTube / Vimeo) */
                <div className="w-full max-w-4xl aspect-video rounded-2xl overflow-hidden border border-white/15 bg-black shadow-[0_20px_60px_rgba(0,0,0,0.9)]">
                  <iframe
                    src={videoSource.embedUrl}
                    title={titleText}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full border-0"
                  />
                </div>
              ) : (
                /* Native HTML5 Video */
                <video
                  key={videoSource.src}
                  src={videoSource.src}
                  poster={posterUrl}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  className="max-h-[60vh] max-w-full w-auto rounded-2xl border border-white/15 bg-black shadow-[0_20px_60px_rgba(0,0,0,0.9)] object-contain"
                >
                  Your browser does not support HTML5 video playback.
                </video>
              )
            ) : (
              /* High-Fidelity Image */
              <img
                key={imageUrl}
                src={imageUrl}
                alt={activeItem.alt_text || titleText}
                className="max-h-[60vh] max-w-full w-auto object-contain rounded-2xl border border-white/15 shadow-[0_20px_60px_rgba(0,0,0,0.9)] transition-all duration-300"
              />
            )}
          </div>
        </div>

        {/* ── Auto-Move Progress Bar ────────────────────────────── */}
        {hasMultiple && (
          <div className="w-full h-1 bg-white/5 overflow-hidden relative">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-cyan-300 transition-all duration-75 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* ── Bottom Section with Prominent Bottom-Middle Navigation ───── */}
        <div className="px-5 py-3.5 sm:px-6 sm:py-4 border-t border-white/10 bg-white/[0.03] flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Left: Title & Description */}
          <div className="min-w-0 max-w-sm space-y-1 text-center md:text-left">
            <h3 className="text-sm sm:text-base font-bold text-white tracking-tight truncate">
              {titleText}
            </h3>
            {descText && (
              <p className="text-xs sm:text-sm text-white/70 line-clamp-2 leading-relaxed">
                {descText}
              </p>
            )}
          </div>

          {/* ── BOTTOM MIDDLE NAVIGATION (PROMINENT, SHOWS CLEARLY) ── */}
          {hasMultiple && (
            <div
              data-element="bottom-middle-nav"
              className="flex items-center gap-2 sm:gap-3 px-4 py-2 rounded-2xl bg-slate-900/90 border border-cyan-400/50 backdrop-blur-xl shadow-[0_0_30px_rgba(6,182,212,0.3)] flex-shrink-0"
            >
              {/* Previous Button */}
              <button
                type="button"
                onClick={handlePrev}
                title="Previous highlight"
                aria-label="Previous highlight"
                className="w-10 h-10 rounded-xl bg-white/10 hover:bg-cyan-400 hover:text-black text-white border border-white/15 flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 cursor-pointer"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Play / Pause Toggle Button */}
              <button
                type="button"
                onClick={() => setIsPaused((prev) => !prev)}
                title={isPaused ? "Resume auto-play" : "Pause auto-play"}
                aria-label={isPaused ? "Resume auto-play" : "Pause auto-play"}
                className={cn(
                  "w-10 h-10 rounded-xl flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-md border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 cursor-pointer",
                  isPaused
                    ? "bg-white/10 text-white hover:bg-white/20 border-white/15"
                    : "bg-cyan-400 text-black hover:bg-cyan-300 border-cyan-300 shadow-[0_0_20px_rgba(6,182,212,0.6)]"
                )}
              >
                {isPaused ? (
                  <Play className="w-4 h-4 ml-0.5 fill-current" />
                ) : (
                  <Pause className="w-4 h-4 fill-current" />
                )}
              </button>

              {/* Slide Counter Pill */}
              <div className="px-3 py-1.5 rounded-xl bg-black/60 border border-white/10 text-xs font-mono font-semibold text-cyan-300 flex items-center gap-1.5 select-none">
                <span>{currentIndex + 1}</span>
                <span className="text-white/30">/</span>
                <span>{items.length}</span>
              </div>

              {/* Next Button */}
              <button
                type="button"
                onClick={handleNext}
                title="Next highlight"
                aria-label="Next highlight"
                className="w-10 h-10 rounded-xl bg-white/10 hover:bg-cyan-400 hover:text-black text-white border border-white/15 flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 cursor-pointer"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Right: Keyboard shortcut hint & featured badge */}
          <div className="flex items-center gap-3 self-center md:self-auto flex-shrink-0 text-right">
            {activeItem.is_featured && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                <Sparkles className="w-3 h-3" />
                <span>Featured</span>
              </span>
            )}
            <span className="hidden sm:inline-block text-[11px] text-white/40 font-mono">
              Use ← / → keys
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
